import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, Repeat, Calendar, Users, DollarSign, AlertCircle, CalendarX, CheckCircle2, MapPin, Clock, Mail, Phone, BellRing,
} from 'lucide-react';
import { seriesAPI, adminAPI } from '../../api/bookings';
import CancelFromDateDialog from './BookingSeries/CancelFromDateDialog';
import { bruneiToday } from '../../lib/brunei';
import { formatDays, formatTimeRange, customerInfo } from './BookingSeries/seriesHelpers';

function bookingStatusBadge(status) {
  const cfg = {
    CONFIRMED: 'bg-emerald-100 text-emerald-800',
    AWAITING_PAYMENT: 'bg-amber-100 text-amber-800',
    CANCELLED: 'bg-rose-100 text-rose-800',
    PAYMENT_FAILED: 'bg-rose-100 text-rose-800',
    PENDING_ALLOCATION: 'bg-slate-100 text-slate-700',
  }[status] || 'bg-slate-100 text-slate-700';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${cfg}`}>{status}</span>;
}

function paymentStatusBadge(paymentStatus) {
  const isPending = (paymentStatus || '').toLowerCase() === 'pending';
  const cls = isPending
    ? 'bg-amber-100 text-amber-800'
    : 'bg-emerald-100 text-emerald-800';
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${cls}`}>
      {isPending ? 'PENDING PAYMENT' : 'PAID'}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    cancelled: { label: 'Cancelled', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  }[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return <Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge>;
}

export default function SeriesDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelFromDate, setShowCancelFromDate] = useState(false);
  const [fromDate, setFromDate] = useState('');
  // Defaults ON — staff opt OUT for the edge case where the customer
  // already requested the cancel by phone. Both buttons share the flag
  // so behaviour stays predictable.
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await seriesAPI.getSeries(id);
      setSeries(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load series');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const today = bruneiToday();
  const futureActiveDates = useMemo(() => {
    if (!series?.bookings) return [];
    return series.bookings
      .filter((b) => b.status !== 'CANCELLED' && b.booking_date >= today)
      .map((b) => b.booking_date)
      .sort();
  }, [series, today]);

  const pendingPaymentCount = useMemo(() => {
    if (!series?.bookings) return 0;
    return series.bookings.filter(
      (b) => b.status !== 'CANCELLED' && (b.payment_status || '').toLowerCase() === 'pending'
    ).length;
  }, [series]);

  const handleCancelAll = async () => {
    const msg = `Cancel ALL ${series.successful_occurrences} bookings in this series? This cannot be undone.`
      + (notifyCustomer ? '\n\nThe customer will be emailed a cancellation summary.' : '\n\nThe customer will NOT be emailed.');
    if (!window.confirm(msg)) return;
    try {
      setSubmitting(true);
      const res = await seriesAPI.cancelSeries(id, { mode: 'all', notify_customer: notifyCustomer });
      toast.success(`Cancelled ${res.cancelled_bookings} booking(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  const openCancelFromDate = () => {
    setFromDate(futureActiveDates[0] || today);
    setShowCancelFromDate(true);
  };

  const handleCancelFromDate = async () => {
    try {
      setSubmitting(true);
      const res = await seriesAPI.cancelSeries(id, {
        mode: 'future', from_date: fromDate, notify_customer: notifyCustomer,
      });
      toast.success(`Cancelled ${res.cancelled_bookings} future booking(s)`);
      setShowCancelFromDate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  const [reminderSendingId, setReminderSendingId] = useState(null);

  const handleSendReminder = async (bookingId) => {
    try {
      setReminderSendingId(bookingId);
      await adminAPI.sendPaymentReminder(bookingId);
      toast.success('Payment reminder sent to customer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminderSendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl">
        <div className="text-center py-8 text-muted-foreground">Loading series…</div>
      </div>
    );
  }
  if (!series) {
    return (
      <div className="max-w-5xl">
        <div className="text-center py-8 text-muted-foreground">Series not found</div>
      </div>
    );
  }

  const isActive = series.status === 'active';
  const ci = customerInfo(series);

  return (
    <div data-testid="series-detail-page" className="max-w-5xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/admin/bookings/series')}
        className="mb-4 -ml-2"
        data-testid="series-back"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to Series
      </Button>

      <Card className="p-6 border-l-4 border-l-emerald-500 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-2">
              <Repeat className="w-5 h-5 text-emerald-600" />
              <h1 className="text-3xl font-black font-heading uppercase">Recurring Series</h1>
              <StatusBadge status={series.status} />
            </div>
            <p className="text-xs font-mono text-muted-foreground mb-3">#{series.id}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p data-testid="series-customer-name">
                <Users className="w-4 h-4 inline mr-1 text-slate-500" /> {ci.name}
              </p>
              <p><MapPin className="w-4 h-4 inline mr-1 text-slate-500" /> {series.location_id}</p>
              {ci.email && (
                <p data-testid="series-customer-email">
                  <Mail className="w-4 h-4 inline mr-1 text-slate-500" /> {ci.email}
                </p>
              )}
              {ci.phone && (
                <p data-testid="series-customer-phone">
                  <Phone className="w-4 h-4 inline mr-1 text-slate-500" /> {ci.phone}
                </p>
              )}
              <p>
                <Calendar className="w-4 h-4 inline mr-1 text-slate-500" />
                {series.recurrence?.start_date} → {series.recurrence?.end_date || `${series.recurrence?.end_after_count} occurrences`}
              </p>
              <p data-testid="series-time-range">
                <Clock className="w-4 h-4 inline mr-1 text-slate-500" />
                {formatTimeRange(series.time_slot, series.duration)} · {series.duration}h
              </p>
              <p className="col-span-2">
                <strong>{series.recurrence?.pattern === 'weekly' ? 'Weekly' : 'Every 2 weeks'}</strong> on {formatDays(series.recurrence?.days_of_week)}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-3xl font-black text-emerald-700">{series.successful_occurrences || 0}</p>
            <p className="text-[11px] uppercase font-bold text-muted-foreground">Bookings</p>
            <p className="text-xs text-muted-foreground mt-2">
              <DollarSign className="w-3 h-3 inline" />
              Est. ${(series.total_revenue_estimate || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {series.skipped_occurrences > 0 && (
          <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
            <AlertCircle className="w-4 h-4 inline mr-1" />
            <strong>{series.skipped_occurrences}</strong> date{series.skipped_occurrences === 1 ? '' : 's'} skipped at creation time
            ({(series.skipped_details || []).map((s) => s.reason).filter(Boolean).join(', ') || 'see details'}).
          </div>
        )}

        {isActive && futureActiveDates.length > 0 && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <label
              className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none"
              data-testid="notify-customer-label"
            >
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                data-testid="notify-customer-checkbox"
                className="w-4 h-4 rounded border-slate-300"
              />
              <span>
                Notify customer by email
                <span className="text-xs text-slate-500 ml-1">
                  (uncheck if already handled offline)
                </span>
              </span>
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={openCancelFromDate}
                data-testid="cancel-from-date-btn"
              >
                Cancel From Date Onward
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                onClick={handleCancelAll}
                disabled={submitting}
                data-testid="cancel-all-btn"
              >
                Cancel Whole Series
              </Button>
            </div>
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-xl font-bold uppercase">Occurrences ({(series.bookings || []).length})</h2>
        {pendingPaymentCount > 0 && (
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800"
            data-testid="occurrences-pending-payment-rollup"
          >
            {pendingPaymentCount} of {(series.bookings || []).length} pending payment
          </span>
        )}
      </div>
      <div className="space-y-2">
        {(series.bookings || []).map((b) => (
          <Card
            key={b.id}
            className="p-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`occurrence-${b.id}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              {b.status === 'CANCELLED'
                ? <CalendarX className="w-4 h-4 text-rose-500" />
                : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              <div>
                <p className="font-bold text-sm">
                  <span className="font-mono" data-testid={`occurrence-ref-${b.id}`}>#{(b.id || '').slice(0, 8)}</span> · {b.booking_date}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatTimeRange(b.time_slot, b.duration)} · {b.duration}H · {(b.courts_assigned || []).map((c) => `Court ${c.court_number}`).join(', ')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-slate-700">${(b.final_total || 0).toFixed(2)}</span>
              {bookingStatusBadge(b.status)}
              {b.status !== 'CANCELLED' && paymentStatusBadge(b.payment_status)}
              {b.status !== 'CANCELLED' && (b.payment_status || '').toLowerCase() === 'pending' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => handleSendReminder(b.id)}
                  disabled={reminderSendingId === b.id}
                  data-testid={`send-reminder-${b.id}`}
                >
                  <BellRing className="w-3.5 h-3.5 mr-1" />
                  {reminderSendingId === b.id ? 'Sending…' : 'Send Reminder'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/admin/bookings?ref=${b.id.slice(0, 8)}`)}
                data-testid={`view-${b.id}`}
              >
                View
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <CancelFromDateDialog
        open={showCancelFromDate}
        onOpenChange={setShowCancelFromDate}
        futureBookingDates={futureActiveDates}
        fromDate={fromDate}
        setFromDate={setFromDate}
        onConfirm={handleCancelFromDate}
        submitting={submitting}
        notifyCustomer={notifyCustomer}
      />
    </div>
  );
}
